import { useEffect, useState, useRef, useCallback } from "react";
import "./App.css";
import AdminPanel from "./AdminPanel";
import { ShareRow } from "./ShareRow";
import { StoryloFab, StoryloCard } from "./Storylo";
import HeroSpotifyCTA from "./HeroSpotifyCTA";
import HdAudiobookBadge from "./HdAudiobookBadge";
import PopularChapterCallout from "./PopularChapterCallout";
import { Dedication, LanguagePicker } from "./Languages";
import LanguageRouterBanner from "./LanguageRouterBanner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Anonymous, persistent visitor id stored in localStorage
function visitorId() {
  let id = localStorage.getItem("tb-visitor-id");
  if (!id) {
    id = (crypto?.randomUUID?.() ||
          Math.random().toString(36).slice(2) + Date.now().toString(36));
    localStorage.setItem("tb-visitor-id", id);
  }
  return id;
}

function postEvent(kind) {
  return fetch(`${API}/stats/event`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ visitor_id: visitorId(), kind }),
  }).catch(() => {});
}

function fmt(d) {
  try {
    const dt = new Date(d);
    return dt.toLocaleDateString(undefined, {
      year: "numeric", month: "short", day: "numeric"
    });
  } catch { return ""; }
}

function App() {
  // Hidden admin route: /admin/{secret} — bypass the normal landing entirely
  const adminMatch =
    typeof window !== "undefined" &&
    window.location.pathname.match(/^\/admin\/([A-Za-z0-9_-]+)\/?$/);
  if (adminMatch) {
    return <AdminPanel secret={adminMatch[1]} />;
  }
  return <Landing />;
}

function Landing() {
  const [items, setItems] = useState([]);
  const [chapters, setChapters] = useState([]);
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    visit: { total: 0, unique: 0 },
    read:  { total: 0, unique: 0 },
    listen:{ total: 0, unique: 0 },
  });
  const [comments, setComments] = useState([]);
  const [commentName, setCommentName] = useState("");
  const [commentMessage, setCommentMessage] = useState("");
  const [commentPosting, setCommentPosting] = useState(false);
  const [commentError, setCommentError] = useState("");
  const [readerOpen, setReaderOpen] = useState(false);
  const [readerPages, setReaderPages] = useState(0);
  const [readerPage, setReaderPage] = useState(1);
  const readerScrollRef = useRef(null);
  const visitTracked = useRef(false);
  const tracked = useRef({ read: new Set(), listen: new Set() });

  const refreshStats = useCallback(() => {
    fetch(`${API}/stats`).then(r => r.json()).then(setStats).catch(() => {});
  }, []);

  const refreshComments = useCallback(() => {
    fetch(`${API}/comments`).then(r => r.json()).then(d => {
      if (Array.isArray(d)) setComments(d);
    }).catch(() => {});
  }, []);

  useEffect(() => {
    // Track a single visit per page load
    if (!visitTracked.current) {
      visitTracked.current = true;
      postEvent("visit").then(refreshStats);
    }

    const load = () => {
      fetch(`${API}/book/manifest`)
        .then((r) => r.json())
        .then((d) => {
          setItems(d.items || []);
          setChapters(d.chapters || []);
          setLoading(false);
        })
        .catch(() => setLoading(false));
      fetch(`${API}/book/status`)
        .then((r) => r.json()).then(setStatus).catch(() => {});
      refreshStats();
      refreshComments();
    };
    load();
    const t = setInterval(load, 12000);
    return () => clearInterval(t);
  }, [refreshStats, refreshComments]);

  const trackDownload = (kind, key) => {
    if (tracked.current[kind].has(key)) return;
    tracked.current[kind].add(key);
    postEvent(kind).then(refreshStats);
  };

  const submitComment = async (e) => {
    e.preventDefault();
    setCommentError("");
    const name = commentName.trim();
    const message = commentMessage.trim();
    if (!name || !message) {
      setCommentError("Please add your name and a message.");
      return;
    }
    setCommentPosting(true);
    try {
      const r = await fetch(`${API}/comments`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ name, message }),
      });
      if (!r.ok) throw new Error("Failed");
      setCommentName("");
      setCommentMessage("");
      await refreshComments();
    } catch {
      setCommentError("Couldn't post your comment. Please try again.");
    } finally {
      setCommentPosting(false);
    }
  };

  const renderStatus = () => {
    if (!status || status.stage === "idle" || status.stage === "done") return null;
    let msg = "";
    if (status.stage === "ocr" || status.stage === "ocr_retry") {
      const pct = Math.round((status.done / status.total) * 100);
      msg = `Indexing manuscript... ${status.done}/${status.total} pages (${pct}%)`;
    } else if (status.stage === "building_text") msg = "Assembling chapter text...";
    else if (status.stage === "generating_audio") msg = `Generating audiobook chapters... (${status.total} total)`;
    else if (status.stage === "tts") {
      const pct = Math.round((status.chapter / status.total) * 100);
      msg = `Narrating ch.${status.chapter}/${status.total}: "${status.current}" (${pct}%)`;
    } else msg = status.stage;
    return (
      <div className="status-bar" data-testid="status-bar">
        <span className="status-dot" />{msg}
      </div>
    );
  };

  const pdfItem = items.find((i) => i.file.endsWith(".pdf"));
  const pdfHref = pdfItem
    ? `${process.env.REACT_APP_BACKEND_URL}${pdfItem.download_url}`
    : null;

  const openReader = () => {
    setReaderOpen(true);
    setReaderPage(1);
    trackDownload("read", "online-reader");
    document.body.style.overflow = "hidden";
    fetch(`${API}/book/pages`)
      .then((r) => r.json())
      .then((d) => setReaderPages(d.pages || 0))
      .catch(() => {});
  };
  const closeReader = () => {
    setReaderOpen(false);
    document.body.style.overflow = "";
  };

  // Track which page the reader is currently looking at via IntersectionObserver
  useEffect(() => {
    if (!readerOpen || !readerScrollRef.current) return;
    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => b.intersectionRatio - a.intersectionRatio)[0];
        if (visible) {
          const n = parseInt(visible.target.dataset.page, 10);
          if (n) setReaderPage(n);
        }
      },
      { root: readerScrollRef.current, threshold: [0.3, 0.6, 0.9] }
    );
    const imgs = readerScrollRef.current.querySelectorAll("[data-page]");
    imgs.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, [readerOpen, readerPages]);

  const jumpToPage = (n) => {
    const clamped = Math.max(1, Math.min(readerPages, n));
    setReaderPage(clamped);
    const el = readerScrollRef.current?.querySelector(`[data-page="${clamped}"]`);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const audioItem = items.find((i) => i.file === "audio/voice_test_fable.mp3");
  const mainItems = items.filter((i) => i.file !== "audio/voice_test_fable.mp3");

  // Map a file to the event kind (read vs listen)
  const kindForFile = (file) => {
    if (file.endsWith(".pdf")) return "read";
    if (file.endsWith(".mp3") || file.endsWith(".zip")) return "listen";
    return null;
  };

  return (
    <div className="app-root">
      <LanguageRouterBanner />
      <section
        className="hero"
        style={{
          backgroundImage:
            "radial-gradient(ellipse at 35% 30%, rgba(0,0,0,0) 0%, rgba(0,0,0,0.55) 70%), url('/cover-bg.jpeg')",
        }}
      >
        <div className="hero-grid">
          <div className="checker" aria-hidden="true">
            <span/><span/><span/><span/><span/><span/><span/><span/>
            <span/><span/><span/><span/><span/><span/><span/><span/>
          </div>
          <div className="vline left" />
          <div className="vline right" />

          <div className="hero-title-wrap">
            <h1 className="hero-title">
              Twice Built,
              <br />
              Twice <em>Broken</em>
            </h1>
          </div>

          <div className="hero-sub">
            <p className="sub-line"><em>A Life of Love, Loss &amp; Reinvention</em></p>
            <p className="sub-line"><em>My Story Told with No Holding Back</em></p>
          </div>

          <div className="hero-byline">
            <span className="byline-text">Aysin Djemil: Founder of Pistachios in the Park</span>
          </div>

          <HdAudiobookBadge />
          <PopularChapterCallout />

          <HeroSpotifyCTA />
        </div>
      </section>

      <main className="content">
        {/* Dedication */}
        <Dedication />

        {/* Primary CTA: read or listen */}
        <section className="primary-cta" data-testid="primary-cta">
          <button
            type="button"
            className="cta-btn cta-primary"
            onClick={openReader}
            disabled={!pdfHref}
            data-testid="read-online-button"
          >
            📖 <span>Read the book</span>
          </button>
          {audioItem && (
            <a
              href="#listen"
              className="cta-btn cta-secondary"
              onClick={(e) => {
                e.preventDefault();
                document.getElementById("listen")?.scrollIntoView({ behavior: "smooth" });
              }}
              data-testid="listen-button"
            >
              🎧 <span>Listen to the audiobook</span>
            </a>
          )}
        </section>

        {/* Engagement counters */}
        <section className="counters" data-testid="counters">
          <div className="counter">
            <div className="counter-num" data-testid="counter-visits-total">{stats.visit.total.toLocaleString()}</div>
            <div className="counter-label">Total visits</div>
            <div className="counter-sub" data-testid="counter-visits-unique">
              {stats.visit.unique.toLocaleString()} unique
            </div>
          </div>
          <div className="counter">
            <div className="counter-num" data-testid="counter-read-total">{stats.read.total.toLocaleString()}</div>
            <div className="counter-label">📖 Read</div>
            <div className="counter-sub" data-testid="counter-read-unique">
              {stats.read.unique.toLocaleString()} unique readers
            </div>
          </div>
          <div className="counter">
            <div className="counter-num" data-testid="counter-listen-total">{stats.listen.total.toLocaleString()}</div>
            <div className="counter-label">🎧 Listened</div>
            <div className="counter-sub" data-testid="counter-listen-unique">
              {stats.listen.unique.toLocaleString()} unique listeners
            </div>
          </div>
        </section>

        {renderStatus()}

        {audioItem && (
          <section className="section voice" id="listen">
            <div className="kicker">Narrated by</div>
            <h2 className="h2"><em>Hear a sample</em></h2>
            <audio
              controls
              src={`${process.env.REACT_APP_BACKEND_URL}${audioItem.download_url}`}
              data-testid="audio-player-voice-sample"
              className="audio-player"
              onPlay={() => trackDownload("listen", "sample")}
            />
            <p className="muted-note">
              Narrated by OpenAI's <em>fable</em> voice — male, British. Full
              audiobook runs <strong>15 hours 52 minutes</strong>. Free to read and listen.
            </p>
          </section>
        )}

        <LanguagePicker />

        <section className="section downloads">
          <div className="kicker">Your files</div>
          <h2 className="h2"><em>Read or listen</em></h2>
          <div className="share-hero" data-testid="share-hero">
            <ShareRow testid="share-hero-row" />
            <span className="share-hero-note">Tell a friend — every share helps the book find its readers.</span>
          </div>
          <StoryloCard />
          {loading && <div className="muted">Loading…</div>}
          <div className="files">
            {mainItems.map((it) => {
              const kind = kindForFile(it.file);
              return (
                <a
                  key={it.file}
                  className="file-card"
                  href={`${process.env.REACT_APP_BACKEND_URL}${it.download_url}`}
                  data-testid={`download-${it.file}`}
                  onClick={() => kind && trackDownload(kind, it.file)}
                >
                  <div className="file-card-left">
                    <div className="file-name">{it.label}</div>
                    <div className="file-meta">{it.file.split('/').pop()} · {it.size_mb} MB</div>
                  </div>
                  <div className="file-card-right">Get ↓</div>
                </a>
              );
            })}
            <a
              className="file-card file-card-zip"
              href={`${process.env.REACT_APP_BACKEND_URL}/api/book/audiobook.zip`}
              data-testid="download-audiobook-zip"
              onClick={() => trackDownload("listen", "audiobook.zip")}
            >
              <div className="file-card-left">
                <div className="file-name">Full audiobook — all 56 chapters</div>
                <div className="file-meta">audiobook.zip · ~1.1 GB · for podcast upload</div>
              </div>
              <div className="file-card-right">Get ↓</div>
            </a>
          </div>

          {chapters.length > 0 && (
            <div className="chapters">
              <div className="kicker tight">{chapters.length} chapters · individual MP3s</div>
              <div className="chapter-grid">
                {chapters.map((c) => {
                  // Per-chapter share text uses chapter label
                  const shareText = `Listening to "${c.label.replace(/^\d+\s*[—–-]?\s*/, "")}" from Twice Built, Twice Broken`;
                  return (
                    <div key={c.file} className="chapter-card" data-testid={`chapter-${c.file}`}>
                      <a
                        className="chapter-card-link"
                        href={`${process.env.REACT_APP_BACKEND_URL}${c.download_url}`}
                        onClick={() => trackDownload("listen", c.file)}
                      >
                        <span className="chapter-name">{c.label}</span>
                        <span className="chapter-size">{c.size_mb}M</span>
                      </a>
                      <ShareRow
                        compact
                        text={shareText}
                        testid={`chapter-share-${c.file}`}
                      />
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </section>

        {/* Comments */}
        <section className="section comments" id="comments" data-testid="comments-section">
          <div className="kicker">Reader voices</div>
          <h2 className="h2"><em>Comments</em></h2>

          <form className="comment-form" onSubmit={submitComment} data-testid="comment-form">
            <input
              className="cf-name"
              type="text"
              placeholder="Your name"
              maxLength={60}
              value={commentName}
              onChange={(e) => setCommentName(e.target.value)}
              data-testid="comment-name-input"
            />
            <textarea
              className="cf-msg"
              placeholder="Share what this story meant to you…"
              maxLength={2000}
              rows={4}
              value={commentMessage}
              onChange={(e) => setCommentMessage(e.target.value)}
              data-testid="comment-message-input"
            />
            <div className="cf-foot">
              <span className="muted">{commentMessage.length}/2000</span>
              <button
                type="submit"
                className="cf-submit"
                disabled={commentPosting}
                data-testid="comment-submit-button"
              >
                {commentPosting ? "Posting..." : "Post comment"}
              </button>
            </div>
            {commentError && <div className="cf-error" data-testid="comment-error">{commentError}</div>}
          </form>

          <div className="comment-list" data-testid="comment-list">
            {comments.length === 0 && (
              <div className="muted comment-empty">
                Be the first to share what this story meant to you.
              </div>
            )}
            {comments.map((c) => (
              <article key={c.id} className="comment" data-testid={`comment-${c.id}`}>
                <header className="comment-head">
                  <span className="comment-author">{c.name}</span>
                  <span className="comment-date">{fmt(c.ts)}</span>
                </header>
                <p className="comment-body">{c.message}</p>
              </article>
            ))}
          </div>
        </section>

        <section className="section">
          <div className="kicker">A note from the author</div>
          <h2 className="h2"><em>About this edition</em></h2>
          <p className="author-note">
            For privacy reasons, certain names have been changed and faces in photographs
            have been obscured. The story, dialog, chapters, introduction and disclaimers
            remain exactly as written.
          </p>
        </section>

        <footer className="footer">
          <span>Aysin Djemil · Twice Built, Twice Broken</span>
          <span className="dot">·</span>
          <span>Founder of Pistachios in the Park</span>
        </footer>
      </main>

      {/* Full-screen reader (image-based, works on every device) */}
      {readerOpen && (
        <div className="reader-overlay" data-testid="reader-overlay" onClick={closeReader}>
          <div className="reader-shell" onClick={(e) => e.stopPropagation()}>
            <header className="reader-bar">
              <div className="reader-title">
                <span className="reader-kicker">Reading</span>
                <span className="reader-name">Twice Built, Twice Broken</span>
              </div>
              <div className="reader-nav">
                <button
                  className="reader-nav-btn"
                  onClick={() => jumpToPage(readerPage - 1)}
                  disabled={readerPage <= 1}
                  data-testid="reader-prev-button"
                  aria-label="Previous page"
                >
                  ‹
                </button>
                <span className="reader-page-info" data-testid="reader-page-info">
                  <input
                    type="number"
                    min={1}
                    max={readerPages}
                    value={readerPage}
                    onChange={(e) => {
                      const v = parseInt(e.target.value, 10);
                      if (!isNaN(v)) jumpToPage(v);
                    }}
                    className="reader-page-input"
                    data-testid="reader-page-input"
                  />
                  <span className="reader-page-total">/ {readerPages}</span>
                </span>
                <button
                  className="reader-nav-btn"
                  onClick={() => jumpToPage(readerPage + 1)}
                  disabled={readerPage >= readerPages}
                  data-testid="reader-next-button"
                  aria-label="Next page"
                >
                  ›
                </button>
              </div>
              <div className="reader-actions">
                <button
                  className="reader-close"
                  onClick={closeReader}
                  data-testid="reader-close-button"
                  aria-label="Close reader"
                >
                  ✕
                </button>
              </div>
            </header>
            <div className="reader-scroll" ref={readerScrollRef} data-testid="reader-scroll">
              {Array.from({ length: readerPages }, (_, i) => i + 1).map((n) => (
                <img
                  key={n}
                  data-page={n}
                  loading="lazy"
                  src={`${API}/book/page/${n}`}
                  alt={`Page ${n}`}
                  className="reader-page"
                />
              ))}
              {readerPages === 0 && (
                <div className="reader-loading">Preparing pages…</div>
              )}
            </div>
          </div>
        </div>
      )}

      <StoryloFab />
    </div>
  );
}

export default App;
